import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { ConnectionManagerContext } from 'contexts/ConnectionManagerContext';
import { useIsTableOnScreen } from 'contexts/TableOnScreenContext';
import { useFeatureFlag } from 'featureFlags';
import { trackPendoEvent } from 'services/PendoService';

import { mapToCard } from 'components/Card';

/**
 * EN-11031: players (mostly older iPads) report the board missing from the felt
 * while seats, bets and action buttons keep updating. Two mechanisms fit and we
 * cannot reproduce either in-house:
 *
 *   data   the client holds no parseable card1..card5 for a street that has
 *          been dealt, so the board renders empty;
 *   paint  the board is in the DOM with faces set and the device never draws
 *          it (the 9/30 Pendo replay, rebuilt from the DOM, "looks fine").
 *
 * This module handles the DATA case, the only one the client can see:
 *
 *   report    once per table and hand, for a table on screen, to Pendo (live
 *             in production builds) and Sentry (only where
 *             REACT_APP_SENTRY_DSN is set, which no shipped build does today).
 *             A report is evidence of a short board in client state. NO report
 *             is inconclusive, not proof of paint: the report is capped per
 *             load, skipped for forks, snapshots and parked tables, and needs a
 *             delivered event. Corroborate with the hand's own data before
 *             calling a silent device a paint failure.
 *   recover   behind `release.board-missing-recovery`: mark the missing slots,
 *             say so beneath the board, and offer the player a bounded
 *             reconnect, which is what delivers a fresh snapshot (see
 *             hooks/useFeltHydrationRecovery).
 *
 * The reconnect is the PLAYER'S, never automatic. A short board on a healthy
 * connection is no evidence the connection is at fault: a shared backend fault
 * would empty every board at once, and an automatic reconnect would then drop
 * and re-subscribe the whole fleet together. A targeted re-subscribe cannot help
 * either: `subscribeGame` only snapshots a NEW subscription (see
 * contexts/tableFeedWatchdog). A tap is spread by people, not clocks, and is
 * still held to a client-wide cooldown and a per-load budget.
 *
 * It reads the same state the board renders from, so it describes what the
 * player was shown, not what the wire sent.
 */

/** How long a short board must persist before it counts as missing. Covers the
 * deal animation and a coalesced burst; a real miss lasts the whole street. */
export const BOARD_DEFICIT_GRACE_MS = 4000;

/** Reports per page load, so a device that misses every hand cannot spend the quota. */
export const BOARD_DEFICIT_REPORT_CAP = 5;

/**
 * The player's reconnect is held to the same shape of bound as
 * ConnectionManager's feed escalation: a client-wide cooldown long enough for
 * one reconnect to prove itself, and a per-load budget — if fresh connections
 * still bring no board, the fault is not this connection and another reconnect
 * cannot help.
 */
export const BOARD_RECOVERY_COOLDOWN_MS = 120_000;
export const BOARD_RECOVERY_BUDGET = 2;

// Client-wide and keyed by table and hand, so every mount of a table (carousel
// panel, grid cell, a table closed and reopened) agrees on which hand it is
// looking at, what has been reported, and whether that hand was reconnected.
const reportedHands = new Set<string>();
const reconnectedHands = new Set<string>();
const tableHands = new Map<string, { serial: number; lastHandNum: number }>();
let recoveriesThisLoad = 0;
let lastRecoveryAtMs = Number.NEGATIVE_INFINITY;

/** Test seam: the caps are module state. */
export const resetBoardDeficitState = () => {
  reportedHands.clear();
  reconnectedHands.clear();
  tableHands.clear();
  recoveriesThisLoad = 0;
  lastRecoveryAtMs = Number.NEGATIVE_INFINITY;
};

const cooldownDeadlineMs = () => lastRecoveryAtMs + BOARD_RECOVERY_COOLDOWN_MS;

const cooldownRemainingMs = () => Math.max(0, cooldownDeadlineMs() - Date.now());

const recoveryAllowed = () =>
  recoveriesThisLoad < BOARD_RECOVERY_BUDGET && cooldownRemainingMs() === 0;

export type BoardSlotState = 'face' | 'facedown' | 'empty' | 'unparseable';

/** A board slot's wire value, classified. Never the value itself. */
export const classifyBoardSlot = (raw: unknown): BoardSlotState => {
  if (raw === null || raw === undefined || raw === '' || raw === '0' || raw === 0) return 'empty';
  if (raw === 'facedown') return 'facedown';
  return mapToCard(raw) ? 'face' : 'unparseable';
};

const CARDS_AT_STAGE = [3, 4, 5];

/**
 * The cards a single live board should be showing, or 0 when there is no claim
 * to make: before the flop, between hands, on an all-fold or inter-hand frame,
 * and on a dealing street not yet seen dealt.
 *
 * Showdown (13) and the winner reveal (14) claim all five whatever this client
 * saw before: the broadcast sends every board card for any hand from the river
 * on (cash-game-broadcast personalizeCommunityCards), so a client that mounted
 * at showdown, or lost the earlier frames to a coalesced burst, is still owed
 * the full board.
 *
 * On a dealing street the claim is capped at the street the hand number has
 * reached. `maxStage` can still belong to the PREVIOUS hand: a client that
 * misses 15/0/1/5 and next sees hand 7 keeps the old river's stage, because
 * HoldEmCards only resets it on a hand number below 6. Uncapped, that new
 * flop would be claimed as owing five cards.
 */
export const expectedBoardCards = (
  handNum: number,
  maxStage: number,
  languageKey: string | undefined
): number => {
  if (!(handNum >= 6 && handNum <= 14)) return 0;
  if (languageKey === 'GAME_MSG_ALLFOLD' || languageKey === 'GAME_MSG_PROCESSING') return 0;
  if (handNum >= 13) return 5;
  const streetOfHand = handNum >= 10 ? 2 : handNum >= 8 ? 1 : 0;
  return CARDS_AT_STAGE[Math.min(maxStage, streetOfHand)] ?? 0;
};

/**
 * What the felt shows for a board the client does not have:
 *   none          nothing extra (board present, not yet confirmed missing,
 *                 table parked, or recovery off)
 *   missing       confirmed missing; the player may reconnect
 *   reconnecting  the connection is coming back
 *   unavailable   missing, and reconnecting will not help now (already tried
 *                 this hand, or the client-wide budget/cooldown says no)
 */
export type BoardRecoveryState = 'none' | 'missing' | 'reconnecting' | 'unavailable';

export type BoardDeficit = {
  state: BoardRecoveryState;
  /** Cards the street is owed; slots past what is shown are the missing ones. */
  expected: number;
  /** The player's reconnect; a no-op unless state is 'missing'. */
  reconnect: () => void;
};

type BoardDeficitInput = {
  /** False for multi-run forks and hand-history snapshots, which this does not model. */
  enabled: boolean;
  handNum: number;
  maxStage: number;
  shownCards: number;
  isVisible: boolean;
  isStaleCards: boolean;
  game: any;
  /** Pendo needs the visitor the event belongs to; it never goes in the payload. */
  playerGUID: string | undefined;
};

/** gameNo when the payload carries one. An empty string is no hand identity. */
const knownGameNo = (gameNo: unknown): string | number | null =>
  gameNo === null || gameNo === undefined || gameNo === '' ? null : (gameNo as string | number);

/**
 * The table's hand count as this client has seen it: bumps whenever the hand
 * number goes backwards, whichever mount sees it first. Kept per table at
 * module level so it survives a remount — closing and reopening a table on the
 * same hand is not a new hand. The fallback for a payload with no gameNo, and a
 * missing gameNo is exactly the data case this exists to catch.
 */
const tableHandSerial = (tableKey: string, handNum: number, advance: boolean): number => {
  const seen = tableHands.get(tableKey);
  if (!seen) {
    if (advance) tableHands.set(tableKey, { serial: 0, lastHandNum: handNum });
    return 0;
  }
  if (!advance) return seen.serial;
  if (handNum < seen.lastHandNum) seen.serial += 1;
  seen.lastHandNum = handNum;
  return seen.serial;
};

/**
 * Which hand this is, as every mount of the table agrees on it. Only an
 * on-screen mount advances the count: a parked or throttled copy replays the
 * same hands late, and interleaving its old hand numbers with the live ones
 * would count one hand twice.
 */
const handKeyFor = (game: any, handNum: number, onScreen: boolean) => {
  const tableKey = String(game?.gameID ?? '?');
  const serial = tableHandSerial(tableKey, handNum, onScreen);
  return `${tableKey}:${knownGameNo(game?.gameNo) ?? `hand-${serial}`}`;
};

type DeficitSnapshot = Omit<BoardDeficitInput, 'enabled' | 'maxStage'> & { expected: number };

const reportBoardDeficit = (now: DeficitSnapshot, handKey: string, recoveryOn: boolean) => {
  const facts = {
    gameID: now.game?.gameID ?? null,
    gameNo: knownGameNo(now.game?.gameNo),
    hand: now.handNum,
    expected: now.expected,
    shown: now.shownCards,
    slots: [1, 2, 3, 4, 5].map((n) => classifyBoardSlot(now.game?.[`card${n}`])).join(','),
    isVisible: now.isVisible,
    isStaleCards: now.isStaleCards,
    languageKey: now.game?.languageKey ?? null,
    hasTabletype: !!now.game?.tabletype,
    hasBlindLevels: !!now.game?.blindLevels,
    recovery: recoveryOn,
    documentVisibility: typeof document === 'undefined' ? null : document.visibilityState
  };

  // App.tsx sets the player's email as the Sentry user for the whole app; this
  // event carries hand facts only, so it is sent with that user cleared.
  Sentry.withScope((scope) => {
    scope.setUser(null);
    scope.setLevel('warning');
    scope.setTags({ area: 'board', boardExpected: String(now.expected) });
    scope.setExtras(facts);
    Sentry.captureMessage('Board short on felt');
  });

  if (now.playerGUID) {
    // Pendo properties are scalars; an unknown fact is left out, not sent as null.
    const properties = Object.fromEntries(
      Object.entries(facts).filter(([, value]) => value !== null && value !== undefined)
    ) as Record<string, string | number | boolean>;
    trackPendoEvent({
      playerGUID: now.playerGUID,
      eventName: 'board_short_on_felt',
      properties,
      dedupeKey: `${now.playerGUID}:board_short_on_felt:${handKey}`
    });
  }
};

/**
 * Watch a live board for a dealt street it is not showing. Once the shortfall
 * outlasts BOARD_DEFICIT_GRACE_MS on a table the player can see, it is
 * reported (once per table and hand) and, with recovery on, answered on the
 * felt with a bounded, player-initiated reconnect.
 */
export const useBoardDeficit = (input: BoardDeficitInput): BoardDeficit => {
  const { enabled, handNum, maxStage, shownCards, game } = input;
  const recoveryOn = useFeatureFlag('boardMissingRecovery');
  const onScreen = useIsTableOnScreen();
  const { reconnect, connectionStatus } = useContext(ConnectionManagerContext);

  const expected = enabled ? expectedBoardCards(handNum, maxStage, game?.languageKey) : 0;
  const isShort = expected > 0 && shownCards < expected;
  const handKey = handKeyFor(game, handNum, onScreen);
  // One key per hand, not per street: a board that stays short from the flop
  // into the turn is one miss, and its grace must not restart on each street.
  // A parked table is not judged at all; its grace starts when it is shown.
  const shortKey = isShort && onScreen ? handKey : null;

  // The timer reports what is on the felt when it fires, not when it was armed.
  const latest = useRef<DeficitSnapshot>({ ...input, expected });
  latest.current = { ...input, expected };
  const recoveryOnRef = useRef(recoveryOn);
  recoveryOnRef.current = recoveryOn;

  const [missingFor, setMissingFor] = useState<string | null>(null);

  useEffect(() => {
    if (!shortKey) {
      setMissingFor(null);
      return;
    }
    const timer = setTimeout(() => {
      setMissingFor(shortKey);
      if (reportedHands.has(handKey) || reportedHands.size >= BOARD_DEFICIT_REPORT_CAP) return;
      reportedHands.add(handKey);
      reportBoardDeficit(latest.current, handKey, recoveryOnRef.current);
    }, BOARD_DEFICIT_GRACE_MS);
    return () => clearTimeout(timer);
    // handKey is fixed for a given shortKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortKey]);

  const missing = shortKey !== null && missingFor === shortKey;

  // The recovery limits are module state other mounts change, so this mount
  // re-renders itself whenever it learns they moved.
  const [, rerender] = useState(0);
  const refresh = useCallback(() => rerender((n) => n + 1), []);

  // Once per hand, across every mount of the table.
  const alreadyReconnected = shortKey !== null && reconnectedHands.has(shortKey);

  const playerReconnect = useCallback(() => {
    if (!missing || !shortKey || reconnectedHands.has(shortKey) || !recoveryAllowed()) {
      // Another table spent the cooldown or budget after this button was drawn:
      // redraw with the truth rather than leave a button that does nothing.
      refresh();
      return;
    }
    recoveriesThisLoad += 1;
    lastRecoveryAtMs = Date.now();
    reconnectedHands.add(shortKey);
    refresh();
    // eslint-disable-next-line no-console
    console.log('[BoardDeficit] player reconnecting for a missing board', {
      gameID: latest.current.game?.gameID
    });
    reconnect();
  }, [missing, shortKey, reconnect, refresh]);

  // Another table's reconnect can hold this one at 'unavailable' on the
  // cooldown alone. Nothing else re-renders a board that stays short, so wake
  // at the deadline. Keyed on the deadline itself: if another reconnect pushes
  // it out while this one waits, the wake re-arms for the new one.
  const waitingOnCooldown =
    missing &&
    recoveryOn &&
    !alreadyReconnected &&
    recoveriesThisLoad < BOARD_RECOVERY_BUDGET &&
    cooldownRemainingMs() > 0;
  const deadline = cooldownDeadlineMs();
  useEffect(() => {
    if (!waitingOnCooldown) return;
    const timer = setTimeout(refresh, Math.max(0, deadline - Date.now()));
    return () => clearTimeout(timer);
  }, [waitingOnCooldown, deadline, refresh]);

  if (!missing || !recoveryOn) return { state: 'none', expected, reconnect: playerReconnect };
  if (connectionStatus === 'reconnecting' || connectionStatus === 'connecting') {
    return { state: 'reconnecting', expected, reconnect: playerReconnect };
  }
  const canReconnect = !alreadyReconnected && recoveryAllowed();
  return { state: canReconnect ? 'missing' : 'unavailable', expected, reconnect: playerReconnect };
};
